import { Search, SlidersHorizontal, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/design-system/components/Button/Button'
import { Drawer } from '@/design-system/components/Drawer/Drawer'
import type { StatusTone } from '@/design-system/components/StatusBadge/StatusBadge'

/* ------------------------------------------------------------------ Onglets de sous-modules */

export interface SubmoduleTab<TKey extends string> {
  key: TKey
  label: string
}

export interface SubmoduleTabsProps<TKey extends string> {
  label: string
  tabs: readonly SubmoduleTab<TKey>[]
  active: TKey
  onChange: (key: TKey) => void
}

/** Onglets soulignés de 44 px, visuellement distincts des vues rapides d'une liste. */
export function SubmoduleTabs<TKey extends string>({
  label,
  tabs,
  active,
  onChange,
}: SubmoduleTabsProps<TKey>) {
  return (
    <div className="ds-submodule-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          role="tab"
          aria-selected={tab.key === active}
          className="ds-submodule-tabs__item"
          onClick={() => {
            onChange(tab.key)
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ Vues rapides */

export interface QuickView<TKey extends string> {
  key: TKey
  label: string
  /** `null` : compteur inconnu (chargement, erreur) — affiché « — », jamais 0. */
  count: number | null
  tone?: StatusTone
}

export interface QuickViewsProps<TKey extends string> {
  views: readonly QuickView<TKey>[]
  active: TKey
  onChange: (key: TKey) => void
  label?: string
}

const numberFormat = new Intl.NumberFormat('fr-CA')

/** Vues de travail de la liste (classe DS `master-tabs`) : 4 à 5 au plus, compteur toujours libellé. */
export function QuickViews<TKey extends string>({
  views,
  active,
  onChange,
  label = 'Vues rapides',
}: QuickViewsProps<TKey>) {
  return (
    <div className="master-tabs ds-quick-views" role="tablist" aria-label={label}>
      {views.map((view) => (
        <button
          key={view.key}
          type="button"
          role="tab"
          aria-selected={view.key === active}
          className="master-tabs__item"
          onClick={() => {
            onChange(view.key)
          }}
        >
          {view.label}
          <span className={`master-tabs__count master-tabs__count--${view.tone ?? 'neutral'}`}>
            {view.count === null ? '—' : numberFormat.format(view.count)}
          </span>
        </button>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ Barre d'outils */

export interface ListToolbarProps {
  search: string
  onSearchChange: (value: string) => void
  searchPlaceholder: string
  /** Nombre de filtres actifs, affiché sur le bouton. */
  activeFilterCount: number
  onOpenFilters: () => void
  /** `false` : la liste n'a aucun filtre au-delà des vues — le bouton n'est pas affiché. */
  filtersAvailable?: boolean
  /** « 9 sur 12 employés »; « Chargement… » ou « Compteur indisponible » selon l'état. */
  counter: ReactNode
  /** Tri explicite (Demandes) — facultatif. */
  sort?: ReactNode
}

/**
 * Recherche locale (« Rechercher dans cette liste », distincte de la recherche globale RECA),
 * bouton Filtres et compteur de résultats sur une même ligne.
 */
export function ListToolbar({
  search,
  onSearchChange,
  searchPlaceholder,
  activeFilterCount,
  onOpenFilters,
  filtersAvailable = true,
  counter,
  sort,
}: ListToolbarProps) {
  return (
    <div className="ds-list-toolbar">
      <label className="ds-list-toolbar__search">
        <Search aria-hidden="true" size={16} />
        <input
          type="search"
          aria-label="Rechercher dans cette liste"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => {
            onSearchChange(event.target.value)
          }}
        />
        {search ? (
          <button
            type="button"
            className="ds-list-toolbar__clear"
            aria-label="Effacer la recherche"
            onClick={() => {
              onSearchChange('')
            }}
          >
            <X aria-hidden="true" size={15} />
          </button>
        ) : null}
      </label>
      {filtersAvailable ? (
        <Button
          type="button"
          variant="secondary"
          size="small"
          icon={SlidersHorizontal}
          aria-haspopup="dialog"
          className="ds-list-toolbar__filters"
          onClick={onOpenFilters}
        >
          {activeFilterCount > 0 ? `Filtres · ${String(activeFilterCount)}` : 'Filtres'}
        </Button>
      ) : null}
      {sort}
      <span className="ds-list-toolbar__counter" aria-live="polite">
        {counter}
      </span>
    </div>
  )
}

/* ------------------------------------------------------------------ Filtres actifs */

export interface ActiveFilter {
  key: string
  label: string
  onRemove: () => void
}

/** Pastilles supprimables et « Tout effacer ». Rien n'est rendu sans filtre actif. */
export function ActiveFilters({
  filters,
  onClearAll,
}: {
  filters: readonly ActiveFilter[]
  onClearAll: () => void
}) {
  if (filters.length === 0) return null
  return (
    <div className="ds-active-filters">
      <span className="ds-active-filters__label">Filtres actifs</span>
      {filters.map((filter) => (
        <button
          key={filter.key}
          type="button"
          className="ds-active-filters__chip"
          aria-label={`Retirer le filtre ${filter.label}`}
          onClick={filter.onRemove}
        >
          {filter.label}
          <X aria-hidden="true" size={13} />
        </button>
      ))}
      <button type="button" className="ds-active-filters__clear" onClick={onClearAll}>
        Tout effacer
      </button>
    </div>
  )
}

/* ------------------------------------------------------------------ Panneau Filtres */

export interface FilterGroup {
  key: string
  label: string
  options: readonly { value: string; label: string }[]
  /** Choix unique : valeur courante, `''` pour « tous ». */
  value: string
  onChange: (value: string) => void
}

export interface FiltersDrawerProps {
  title: string
  groups: readonly FilterGroup[]
  onClearAll: () => void
  onClose: () => void
}

/**
 * Panneau latéral des filtres : groupes de pastilles basculantes (`aria-pressed`). Les filtres
 * s'appliquent à l'ensemble des résultats, pas à la seule page affichée.
 */
export function FiltersDrawer({ title, groups, onClearAll, onClose }: FiltersDrawerProps) {
  return (
    <Drawer
      title={title}
      onClose={onClose}
      footer={
        <div className="ds-filters-drawer__footer">
          <Button type="button" variant="ghost" onClick={onClearAll}>
            Tout effacer
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Afficher les résultats
          </Button>
        </div>
      }
    >
      <div className="ds-filters-drawer">
        {groups.map((group) => (
          <fieldset key={group.key} className="ds-filters-drawer__group">
            <legend>{group.label}</legend>
            <div className="ds-filters-drawer__options">
              {group.options.map((option) => {
                const pressed = group.value === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={pressed}
                    className="ds-filters-drawer__option"
                    onClick={() => {
                      group.onChange(pressed ? '' : option.value)
                    }}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
          </fieldset>
        ))}
        <p className="ds-filters-drawer__note">
          Les filtres s’appliquent à l’ensemble des résultats et sont conservés au retour d’une fiche.
        </p>
      </div>
    </Drawer>
  )
}

/* ------------------------------------------------------------------ Pied de liste */

/**
 * Pied d'une liste non paginée : même emplacement et même gabarit que `Pagination`
 * (`master-pagination`), pour que le nombre de résultats reste toujours au même endroit.
 */
export function ListFooter({ children }: { children: ReactNode }) {
  return (
    <div className="master-pagination ds-list-footer">
      <span aria-live="polite">{children}</span>
    </div>
  )
}
